/** @jsxImportSource theme-ui @jsxRuntime classic */
import React from 'react';

import { Text, Flex, Box, Link } from '..';
import { formatPhoneNumberAsLink } from '../utils';

import type { ThemeUIComponentProps } from '..';

export interface FooterContactProps extends ThemeUIComponentProps {
  address: string;
  phoneNumberOne: string;
  phoneNumberTwo: string;
  copyright: string;
  footerContactSubNavLinks: {
    label: string;
    url: string;
  }[];
}

export const FooterContact = ({
  address,
  copyright,
  phoneNumberOne,
  phoneNumberTwo,
  footerContactSubNavLinks,
  sx,
  ...props
}: FooterContactProps): JSX.Element => {
  const currentYear = new Date().getFullYear();
  const copyrightWithCurrentYear = copyright.replace(
    '{YEAR}',
    `${currentYear}`,
  );

  return (
    <Flex
      {...props}
      sx={{
        flexWrap: 'wrap',
        fontSize: 2,
        color: 'text',
        bg: 'background',
        py: 4,
        justifyContent: 'space-between',
        alignItems: ['flex-start', 'flex-start', 'flex-start', 'center'],
        flexDirection: ['column', null, null, 'row'],
        lineHeight: ['footer', 'inherit'],
        ...sx,
      }}
    >
      <Box>
        <Flex
          sx={{
            flexDirection: ['column', null, null, 'row'],
            columnGap: '20px',
            maxWidth: ['200px', null, null, 'initial'],
          }}
        >
          <Text>{address}</Text>

          {[phoneNumberOne, phoneNumberTwo].map((phoneNumber) => (
            <Link
              href={formatPhoneNumberAsLink(phoneNumber)}
              key={phoneNumber}
              sx={{
                'variant': 'text.hoursAndDirections',
                'display': 'block',
                'fontSize': 16,
                'textDecoration': 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              target="_self"
            >
              {phoneNumber}
            </Link>
          ))}
        </Flex>
      </Box>

      <Flex
        sx={{
          flexDirection: ['column', null, null, 'row'],
          flexGrow: 1,
          justifyContent: 'space-evenly',
        }}
      >
        {footerContactSubNavLinks &&
          footerContactSubNavLinks.map(({ label, url }) => (
            <Link href={url} key={label} variant="footer">
              {label === 'Privacy Policy'
                ? 'Privacy Policy'
                : 'Terms & Conditions'}
            </Link>
          ))}
      </Flex>

      <Box
        sx={{
          mt: [4, null, null, 0],
          maxWidth: ['200px', null, null, 'initial'],
        }}
      >
        <Text>
          {copyrightWithCurrentYear}
          ALL RIGHTS RESERVED
        </Text>
      </Box>
    </Flex>
  );
};
